import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import BackButton from '../../../ui/BackButton';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  styled,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
  Popper,
  Paper as MuiPaper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Spellcheck as SpellcheckIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Comprehensive dictionary of correct designations and their common misspellings
const DESIGNATION_CORRECTIONS = {
  // Common roles with their misspellings
  'counsellor': ['couselr', 'counselr', 'councellor', 'counsler', 'counselor', 'counsellor'],
  'counselor': ['couselr', 'councelor', 'councellor', 'counsler'],
  'manager': ['manger', 'maneger', 'mangr', 'managr', 'mnaager'],
  'assistant': ['assisstant', 'assitent', 'assistantt', 'asistant', 'assintant'],
  'associate': ['associat', 'associatee', 'asociate', 'assoiate'],
  'executive': ['executiv', 'executve', 'exective', 'exec', 'executieve'],
  'director': ['directer', 'dirctor', 'director', 'direktor', 'driector'],
  'supervisor': ['superviser', 'supervisr', 'supevisor', 'supervizor'],
  'coordinator': ['coordinater', 'co-ordinator', 'coordinator', 'cordinator', 'coordinatior'],
  'specialist': ['specialistt', 'specilist', 'specialist', 'spcialist'],
  'analyst': ['analist', 'analystt', 'anaylst', 'analitic'],
  'consultant': ['consulatant', 'consultent', 'consutant', 'consiltant'],
  'engineer': ['enginner', 'engeneer', 'enginerr', 'engeener', 'enjinier'],
  'developer': ['developr', 'devloper', 'develope', 'develper', 'devlpr'],
  'architect': ['architecht', 'archtect', 'architectt', 'architec'],
  'designer': ['desinger', 'desgner', 'designer', 'desiner'],
  'administrator': ['administrater', 'admin', 'administator', 'adminstrator'],
  'technician': ['techician', 'technitian', 'techinician', 'tecknician'],
  'operator': ['operater', 'operater', 'oprater', 'operatoer'],
  'coordinator': ['coordinater', 'cordinator', 'coordiantor'],
  
  // HR & Administrative
  'human resources': ['hr', 'human resource', 'humen resources', 'human resourses'],
  'recruiter': ['recruter', 'recruiterr', 'recruitor', 'recruter'],
  'payroll': ['payrole', 'payrol', 'payrll'],
  'compliance': ['complacence', 'complience', 'complaiance'],
  
  // IT & Technical
  'software engineer': ['software enginner', 'soft engineer', 'software engg'],
  'system administrator': ['system admin', 'sys admin', 'system administrater'],
  'database administrator': ['dba', 'database admin', 'data base administrator'],
  'network engineer': ['network enginner', 'netwrk engineer', 'netwrok engineer'],
  'security analyst': ['security analist', 'sec analyst', 'security analitic'],
  'devops engineer': ['devops enginner', 'dev ops engineer', 'devop engineer'],
  'cloud architect': ['cloud architecht', 'cloud arcitect', 'cloud archtect'],
  'full stack developer': ['fullstack developer', 'full stack devloper', 'full-stack developer'],
  'frontend developer': ['front end developer', 'frontend devloper', 'frond end developer'],
  'backend developer': ['back end developer', 'backend devloper', 'back-end developer'],
  
  // Finance & Accounting
  'accountant': ['accountent', 'accoutant', 'accountantt', 'accountan'],
  'finance manager': ['finance manger', 'financial manager', 'finace manager'],
  'auditor': ['auditer', 'auditorr', 'auditoor'],
  'treasurer': ['treasurer', 'tresurer', 'treasuror'],
  
  // Marketing & Sales
  'marketing manager': ['marketing manger', 'marketng manager', 'markting manager'],
  'sales manager': ['sales manger', 'sale manager', 'salse manager'],
  'business development': ['bussiness development', 'business dev', 'business developement'],
  'product manager': ['product manger', 'prod manager', 'produkt manager'],
  'project manager': ['project manger', 'proj manager', 'projectt manager'],
  
  // Customer Support
  'customer support': ['customer supportt', 'cust support', 'customer suport'],
  'customer service': ['customer servise', 'cust service', 'customer servece'],
  'help desk': ['helpdesk', 'hel desk', 'help desck'],
  'support specialist': ['support specialistt', 'suport specialist', 'support specilist'],
  
  // Operations
  'operations manager': ['operations manger', 'operation manager', 'ops manager'],
  'logistics coordinator': ['logistics coordinater', 'logistic coordinator', 'logistis coordinator'],
  'supply chain': ['supply chainn', 'suplly chain', 'supply chaine'],
  'warehouse manager': ['warehouse manger', 'warehous manager', 'werehouse manager'],
  
  // Quality & Safety
  'quality assurance': ['qa', 'quality assuramce', 'quality asurance'],
  'quality control': ['qc', 'quality controll', 'quality cntrol'],
  'safety officer': ['safety oficer', 'saftey officer', 'safety officerr'],
  
  // Education & Training
  'trainer': ['trainer', 'trainor', 'traineer'],
  'instructor': ['instructer', 'instruktor', 'instructorr'],
  'teacher': ['techer', 'teecher', 'teachar'],
  'professor': ['profesor', 'professer', 'proffesor'],
};

// Flatten the dictionary for quick lookup
const SPELLING_SUGGESTIONS = {};
Object.entries(DESIGNATION_CORRECTIONS).forEach(([correct, misspellings]) => {
  misspellings.forEach(misspelling => {
    SPELLING_SUGGESTIONS[misspelling.toLowerCase()] = correct;
  });
  // Add the correct word itself
  SPELLING_SUGGESTIONS[correct.toLowerCase()] = correct;
});

// Common prefixes and suffixes for better matching
const COMMON_PREFIXES = ['senior', 'junior', 'associate', 'assistant', 'deputy', 'chief', 'head', 'lead', 'principal'];
const COMMON_SUFFIXES = ['manager', 'director', 'officer', 'coordinator', 'specialist', 'analyst', 'engineer'];

// Function to calculate Levenshtein distance for fuzzy matching
const levenshteinDistance = (str1, str2) => {
  const track = Array(str2.length + 1).fill(null).map(() =>
    Array(str1.length + 1).fill(null));
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1,
        track[j - 1][i] + 1,
        track[j - 1][i - 1] + indicator,
      );
    }
  }
  return track[str2.length][str1.length];
};

// Enhanced function to get spelling suggestions with better matching
const getEnhancedSpellingSuggestions = (word) => {
  if (!word || word.length < 2) return [];
  
  const lowerWord = word.toLowerCase().trim();
  const suggestions = new Map(); // Use Map to store suggestion with score
  
  // 1. Check direct mapping in dictionary
  if (SPELLING_SUGGESTIONS[lowerWord]) {
    suggestions.set(SPELLING_SUGGESTIONS[lowerWord], 100);
  }
  
  // 2. Check for partial matches (word contains part of dictionary word or vice versa)
  Object.keys(SPELLING_SUGGESTIONS).forEach(dictWord => {
    if (dictWord.includes(lowerWord) && dictWord.length > lowerWord.length + 2) {
      const score = 85;
      if (!suggestions.has(SPELLING_SUGGESTIONS[dictWord]) || suggestions.get(SPELLING_SUGGESTIONS[dictWord]) < score) {
        suggestions.set(SPELLING_SUGGESTIONS[dictWord], score);
      }
    } else if (lowerWord.includes(dictWord) && lowerWord.length > dictWord.length + 2) {
      const score = 80;
      if (!suggestions.has(SPELLING_SUGGESTIONS[dictWord]) || suggestions.get(SPELLING_SUGGESTIONS[dictWord]) < score) {
        suggestions.set(SPELLING_SUGGESTIONS[dictWord], score);
      }
    }
  });
  
  // 3. Fuzzy matching with Levenshtein distance
  Object.keys(SPELLING_SUGGESTIONS).forEach(dictWord => {
    if (Math.abs(dictWord.length - lowerWord.length) <= 3) {
      const distance = levenshteinDistance(lowerWord, dictWord);
      const maxDistance = Math.max(1, Math.floor(Math.min(lowerWord.length, dictWord.length) / 2.5));
      
      if (distance <= maxDistance && distance > 0) {
        const score = Math.max(0, 100 - (distance / maxDistance) * 30);
        const correctWord = SPELLING_SUGGESTIONS[dictWord];
        if (!suggestions.has(correctWord) || suggestions.get(correctWord) < score) {
          suggestions.set(correctWord, score);
        }
      }
    }
  });
  
  // 4. Check for common typos (character frequency and position)
  Object.entries(SPELLING_SUGGESTIONS).forEach(([dictWord, correctWord]) => {
    if (Math.abs(dictWord.length - lowerWord.length) <= 2) {
      let similarity = 0;
      const maxLen = Math.max(dictWord.length, lowerWord.length);
      
      // Check character frequency
      const freqLower = {};
      const freqDict = {};
      for (let char of lowerWord) freqLower[char] = (freqLower[char] || 0) + 1;
      for (let char of dictWord) freqDict[char] = (freqDict[char] || 0) + 1;
      
      let commonChars = 0;
      for (let char in freqLower) {
        if (freqDict[char]) {
          commonChars += Math.min(freqLower[char], freqDict[char]);
        }
      }
      
      const freqScore = (commonChars / maxLen) * 100;
      
      if (freqScore > 60 && !suggestions.has(correctWord)) {
        suggestions.set(correctWord, freqScore);
      }
    }
  });
  
  // 5. Suggest adding common prefixes if appropriate
  if (lowerWord.length >= 3) {
    COMMON_PREFIXES.forEach(prefix => {
      if (prefix.includes(lowerWord) || lowerWord.includes(prefix.slice(0, 3))) {
        COMMON_SUFFIXES.forEach(suffix => {
          const suggestion = `${prefix} ${suffix}`;
          if (!suggestions.has(suggestion)) {
            suggestions.set(suggestion, 70);
          }
        });
      }
    });
  }
  
  // Convert to array and sort by score (highest first)
  const sortedSuggestions = Array.from(suggestions.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([suggestion]) => suggestion)
    .slice(0, 5); // Limit to top 5 suggestions
  
  // Capitalize each word in suggestion
  return sortedSuggestions.map(suggestion => 
    suggestion.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  );
};

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Designation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddDesignationModal, setShowAddDesignationModal] = useState(false);
  const [showEditDesignationModal, setShowEditDesignationModal] = useState(false);
  const [designationName, setDesignationName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState({ desigId: null, designationName: '', deptId: null });
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Spelling suggestion states
  const [suggestions, setSuggestions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [originalText, setOriginalText] = useState('');
  const [currentWord, setCurrentWord] = useState('');
  
  // Suggestion-applied snackbar state
  const [showAutoCorrectSnackbar, setShowAutoCorrectSnackbar] = useState(false);
  const [autoCorrectMessage, setAutoCorrectMessage] = useState('');

  // Fetch all departments
  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/departments/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      setDepartments(response.data);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch departments.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch all designations
  const fetchDesignations = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/designations/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const mappedDesignations = response.data.map(desig => ({
        desigId: desig.id,
        deptId: desig.department,
        departmentName: departments.find(dept => dept.id === desig.department)?.name || 'Unknown',
        designationName: desig.name,
      }));
      setDesignations(mappedDesignations);
      setFilteredData(mappedDesignations);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch designations.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle designation name change - shows suggestions only, never changes the text
  const handleDesignationNameChange = (e) => {
    const value = e.target.value;
    setDesignationName(value);
    
    if (value && value.trim().length > 1) {
      // Get the current word being typed (last word)
      const words = value.split(' ');
      const lastWord = words[words.length - 1];
      setCurrentWord(lastWord);
      
      if (lastWord.length >= 2) {
        // Get suggestions for the last word, hiding ones identical to what the user typed
        const suggestionsList = getEnhancedSpellingSuggestions(lastWord).filter(
          (s) => s.toLowerCase() !== lastWord.toLowerCase()
        );
        
        if (suggestionsList.length > 0) {
          setSuggestions(suggestionsList);
          setAnchorEl(e.currentTarget);
          setShowSuggestions(true);
          setOriginalText(value);
          return;
        }
      }
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };
  
  // Apply a specific suggestion (only when the user clicks it)
  const applySuggestion = (suggestion) => {
    const words = designationName.split(' ');
    words[words.length - 1] = suggestion;
    const newText = words.join(' ');
    setDesignationName(newText);
    setShowSuggestions(false);
    setSuggestions([]);
    setAutoCorrectMessage(`✓ Applied suggestion: "${currentWord}" → "${suggestion}"`);
    setShowAutoCorrectSnackbar(true);
    setTimeout(() => setShowAutoCorrectSnackbar(false), 3000);
  };

  // Add a new designation
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Use the text exactly as the user typed it
    const finalName = designationName.trim();
    
    if (!departmentId || !finalName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Department and Designation Name are required.');
      setOpenSnackbar(true);
      return;
    }
    
    // Check for duplicate designation
    const isDuplicate = designations.some(
      desig => desig.designationName.toLowerCase() === finalName.toLowerCase() && desig.deptId === departmentId
    );
    
    if (isDuplicate) {
      setSnackbarSeverity('error');
      setSnackbarMessage('This designation already exists in the selected department!');
      setOpenSnackbar(true);
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/master/designations/`,
        { name: finalName, department: departmentId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const newDesignation = {
        desigId: response.data.id,
        deptId: response.data.department,
        departmentName: departments.find(dept => dept.id === response.data.department)?.name || 'Unknown',
        designationName: response.data.name,
      };
      setDesignations([...designations, newDesignation]);
      setFilteredData([...filteredData, newDesignation]);
      setShowAddDesignationModal(false);
      setSnackbarMessage('Designation added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setDesignationName('');
      setDepartmentId('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to add designation.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a designation
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/designations/${selectedDesignation.desigId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const updatedDesignations = designations.filter((desig) => desig.desigId !== selectedDesignation.desigId);
      setDesignations(updatedDesignations);
      setFilteredData(updatedDesignations);
      setShowDeleteModal(false);
      setSnackbarMessage('Designation deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete designation.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Edit a designation
  const handleUpdate = async () => {
    // Use the text exactly as the user typed it
    const finalName = designationName.trim();
    
    if (!departmentId || !finalName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Department and Designation Name are required.');
      setOpenSnackbar(true);
      return;
    }
    
    // Check for duplicate designation (excluding current one)
    const isDuplicate = designations.some(
      desig => desig.designationName.toLowerCase() === finalName.toLowerCase() && 
               desig.deptId === departmentId && 
               desig.desigId !== selectedDesignation.desigId
    );
    
    if (isDuplicate) {
      setSnackbarSeverity('error');
      setSnackbarMessage('This designation already exists in the selected department!');
      setOpenSnackbar(true);
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/master/designations/${selectedDesignation.desigId}/`,
        { name: finalName, department: departmentId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const updatedDesignation = {
        desigId: response.data.id,
        deptId: response.data.department,
        departmentName: departments.find(dept => dept.id === response.data.department)?.name || 'Unknown',
        designationName: response.data.name,
      };
      const updatedDesignations = designations.map((desig) =>
        desig.desigId === selectedDesignation.desigId ? updatedDesignation : desig
      );
      setDesignations(updatedDesignations);
      setFilteredData(updatedDesignations);
      setShowEditDesignationModal(false);
      setSnackbarMessage('Designation updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setDesignationName('');
      setDepartmentId('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update designation.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddDesignation = () => {
    setDesignationName('');
    setDepartmentId('');
    setShowAddDesignationModal(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleEdit = (designation) => {
    setSelectedDesignation({ desigId: designation.desigId, designationName: designation.designationName, deptId: designation.deptId });
    setDesignationName(designation.designationName);
    setDepartmentId(designation.deptId);
    setShowEditDesignationModal(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleShowDeleteModal = (desigId, designationName) => {
    setSelectedDesignation({ desigId, designationName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleReset = () => {
    setDesignationName('');
    setDepartmentId('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleCancel = () => {
    setShowEditDesignationModal(false);
    setDesignationName('');
    setDepartmentId('');
    setSuggestions([]);
    setShowSuggestions(false);
  };
  
  const handleCloseSuggestions = () => {
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

 useEffect(() => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    navigate('/login');
    return;
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const deptResponse = await axios.get(`${API_BASE_URL}/api/master/departments/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const fetchedDepartments = deptResponse.data;
      setDepartments(fetchedDepartments);

      const desigResponse = await axios.get(`${API_BASE_URL}/api/master/designations/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const mappedDesignations = desigResponse.data.map(desig => ({
        desigId: desig.id,
        deptId: desig.department,
        departmentName: fetchedDepartments.find(dept => dept.id === desig.department)?.name || 'Unknown',
        designationName: desig.name,
      }));
      setDesignations(mappedDesignations);
      setFilteredData(mappedDesignations);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch data.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  useEffect(() => {
    setFilteredData(
      designations.filter(
        (desig) =>
          desig.departmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          desig.designationName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, designations]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddDesignation}
            sx={{
              ml: 2,
              width: '180px',
              height: '50px',
              borderRadius: '10px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD DESIGNATION
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Department Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Designation Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((desig, index) => (
                    <TableRow
                      key={desig.desigId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{desig.departmentName}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{desig.designationName}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(desig)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleShowDeleteModal(desig.desigId, desig.designationName)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={4} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Suggestion-applied notification snackbar */}
      <Snackbar
        open={showAutoCorrectSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowAutoCorrectSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: '100%', backgroundColor: theme.palette.info.main }}>
          <SpellcheckIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} /> {autoCorrectMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedDesignation.designationName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Designation Modal */}
      <Modal
        open={showAddDesignationModal}
        onClose={() => setShowAddDesignationModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Designation
            </Typography>
            <IconButton onClick={() => setShowAddDesignationModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="department-label" sx={{ color: theme.palette.text.primary }}>
                Department
              </InputLabel>
              <Select
                labelId="department-label"
                id="department"
                value={departmentId}
                label="Department"
                onChange={(e) => setDepartmentId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ position: 'relative', mb: 2 }}>
              <TextField
                label="Designation Name"
                variant="outlined"
                fullWidth
                value={designationName}
                onChange={handleDesignationNameChange}
                FormHelperTextProps={{
                  sx: {
                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.6)',
                    fontWeight: 500
                  }
                }}
                helperText={
                  <span>
                    <SpellcheckIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    Suggestions appear as you type. Click one to use it, or keep your own spelling.
                  </span>
                }
                sx={{
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
              
              {/* Suggestions Popper */}
              <Popper
                open={showSuggestions}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300, width: anchorEl?.clientWidth }}
              >
                <ClickAwayListener onClickAway={handleCloseSuggestions}>
                  <MuiPaper elevation={3} sx={{ mt: 0.5, maxHeight: 250, overflow: 'auto' }}>
                    <List dense>
                      <ListItem sx={{ backgroundColor: theme.palette.action.hover }}>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AutoFixIcon fontSize="small" color="primary" />
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                Spelling Suggestions for "{currentWord}"
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {suggestions.map((suggestion, idx) => (
                        <ListItem 
                          key={idx} 
                          button 
                          onClick={() => applySuggestion(suggestion)}
                          sx={{
                            '&:hover': {
                              backgroundColor: theme.palette.action.selected,
                            },
                          }}
                        >
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={idx === 0 ? "Best Match" : "Alternative"} 
                                  size="small" 
                                  color={idx === 0 ? "primary" : "default"}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                                <Typography variant="body2">{suggestion}</Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </MuiPaper>
                </ClickAwayListener>
              </Popper>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Tooltip title={!departmentId ? "Select Department" : !designationName ? "Enter Designation Name" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!departmentId || !designationName}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
{/* Edit Designation Modal */}
<Modal
  open={showEditDesignationModal}
  onClose={handleCancel}
  BackdropProps={{
    style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  }}
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 500,
      bgcolor: "background.paper",
      color: theme.palette.text.primary,
      borderRadius: 2,
      boxShadow: 24,
      overflow: "hidden",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: "12px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.primary.contrastText,
          fontWeight: "bold",
        }}
      >
        Edit Designation
      </Typography>

      <IconButton
        onClick={handleCancel}
        sx={{
          color: theme.palette.primary.contrastText,
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Body */}
    <Box sx={{ padding: "20px" }}>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel
          id="department-label-edit"
          sx={{
            color: theme.palette.text.primary,
            "&.Mui-focused": {
              color: theme.palette.primary.main,
            },
          }}
        >
          Department
        </InputLabel>

        <Select
          labelId="department-label-edit"
          id="department-edit"
          value={departmentId}
          label="Department"
          onChange={(e) => setDepartmentId(e.target.value)}
          sx={{
            color: theme.palette.text.primary,

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },

            "& .MuiSvgIcon-root": {
              color: theme.palette.text.primary,
            },
          }}
        >
          {departments.map((department) => (
            <MenuItem
              key={department.id}
              value={department.id}
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {department.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ position: "relative", mb: 2 }}>
        <TextField
          label="Designation Name"
          variant="outlined"
          fullWidth
          value={designationName}
          onChange={handleDesignationNameChange}
          helperText={
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.primary,
              }}
            >
              <SpellcheckIcon
                sx={{
                  fontSize: 14,
                  mr: 0.5,
                  color: theme.palette.primary.contrastText,
                }}
              />

              Suggestions appear as you type. Click one to use it, or keep your own spelling.
            </Box>
          }
          sx={{
            "& .MuiInputLabel-root": {
              color: theme.palette.text.primary,
            },

            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.primary.main,
            },

            "& .MuiOutlinedInput-root": {
              color: theme.palette.text.primary,

              "& fieldset": {
                borderColor: theme.palette.divider,
              },

              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },

              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },

            "& .MuiFormHelperText-root": {
              color: theme.palette.text.secondary,
            },
          }}
        />

        {/* Suggestions Popper */}
        <Popper
          open={showSuggestions}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{
            zIndex: 1300,
            width: anchorEl?.clientWidth,
          }}
        >
          <ClickAwayListener onClickAway={handleCloseSuggestions}>
            <MuiPaper
              elevation={3}
              sx={{
                mt: 0.5,
                maxHeight: 250,
                overflow: "auto",
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <List dense sx={{ p: 0 }}>
                {/* Suggestion Header */}
                <ListItem
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <AutoFixIcon
                          fontSize="small"
                          sx={{
                            color: theme.palette.primary,
                          }}
                        />

                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: theme.palette.text.primary,
                          }}
                        >
                          Spelling Suggestions for "{currentWord}"
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                {/* Suggestions */}
                {suggestions.map((suggestion, idx) => (
                  <ListItem
                    key={idx}
                    button
                    onClick={() => applySuggestion(suggestion)}
                    sx={{
                      color: theme.palette.text.primary,

                      "&:hover": {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={
                              idx === 0
                                ? "Best Match"
                                : "Alternative"
                            }
                            size="small"
                            color={
                              idx === 0 ? "primary" : "default"
                            }
                            sx={{
                              fontSize: "0.7rem",
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                            }}
                          >
                            {suggestion}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MuiPaper>
          </ClickAwayListener>
        </Popper>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mt: 2,
        }}
      >
        <Tooltip
          title={
            !departmentId
              ? "Select Department"
              : !designationName
              ? "Enter Designation Name"
              : ""
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,

                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              disabled={!departmentId || !designationName}
            >
              UPDATE
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          color="error"
          onClick={handleCancel}
        >
          CANCEL
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>
      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 900,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Designation Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              💡 Spelling Suggestions
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                <strong>Intelligent Spelling Suggestions:</strong> The system detects common spelling mistakes and suggests corrections. Nothing is changed unless you choose it.
              </li>
              <li>
                <strong>Example:</strong> If you type "<strong>couselr</strong>", it will suggest "<strong>Counsellor</strong>" or "<strong>Counselor</strong>".
              </li>
              <li>
                <strong>Real-time Suggestions:</strong> As you type, a dropdown appears with the best matching suggestions.
              </li>
              <li>
                <strong>Best Match Indicator:</strong> The top suggestion is marked as "Best Match" for quick selection.
              </li>
              <li>
                <strong>One-Click Selection:</strong> Click any suggestion to use it, or ignore the dropdown to keep your own spelling.
              </li>
              <li>
                <strong>Visual Feedback:</strong> A notification confirms when a suggestion is applied.
              </li>
            </ul>
            
            <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: theme.palette.info.light + '20', borderRadius: 2 }}>
              💡 <strong>Pro Tip:</strong> The suggestions understand common designation terms. For example:
              <br />• "couselr" → "Counsellor" or "Counselor"
              <br />• "manger" → "Manager"
              <br />• "assisstant" → "Assistant"
              <br />• "enginner" → "Engineer"
              <br />• "devloper" → "Developer"
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              📋 Common Suggestion Examples
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">Common Typos → Suggested</Typography>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                  <li>couselr → Counsellor</li>
                  <li>manger → Manager</li>
                  <li>assisstant → Assistant</li>
                  <li>enginner → Engineer</li>
                  <li>devloper → Developer</li>
                  <li>directer → Director</li>
                  <li>superviser → Supervisor</li>
                </ul>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">More Examples</Typography>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                  <li>analist → Analyst</li>
                  <li>consulatant → Consultant</li>
                  <li>architecht → Architect</li>
                  <li>technitian → Technician</li>
                  <li>administrater → Administrator</li>
                  <li>coordinater → Coordinator</li>
                </ul>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Step-by-Step Guide: Adding a Designation
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>"ADD DESIGNATION"</strong> button.</li>
              <li>Select a <strong>Department</strong> from the dropdown.</li>
              <li>Type the <strong>Designation Name</strong> (e.g., "seniour couselr").</li>
              <li>Watch as suggestions appear below the field.</li>
              <li>Click a <strong>suggestion</strong> to use it, or keep what you typed.</li>
              <li>Review the text and click <strong>"SAVE"</strong>.</li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Editing Existing Designations
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Edit icon</strong> (pencil) next to any designation.</li>
              <li>The same spelling suggestions are available in the edit dialog.</li>
              <li>Make your changes, pick a suggestion if you want one, and click <strong>"UPDATE"</strong>.</li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Searching for Designations
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Use the search bar to filter designations by department or name.</li>
              <li>The search is case-insensitive and updates in real-time.</li>
            </ul>
          </CustomTableScrollbar>
        </Box>
      </Modal>

    </Container>
  );
};

export default Designation;